/* assets/gcitypartners-capacity-activation.js — GCITYPARTNERS-SUBCONTRACT-ACTIVATION-P2
 * Prime -> Subcontract relationship orchestrator.
 * Creates one canonical child delivery_jobs work package through the native GCityPartners facade,
 * then hands explicit RFQ submit back to the existing Supplier Network / #768 RFQ owner.
 */
(function (window, document) {
  'use strict';

  var ROOT = '[data-gcp-supplier-network]';
  var API_ORIGIN = 'https://api.glhksolution.com';
  var API_PREFIX = '/v1/gcitypartners/';
  var mounted = new WeakSet();

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }
  function isZh() {
    return /^zh/i.test(clean(document.documentElement.lang));
  }
  /**
   * @param {string} zh
   * @param {string} en
   */
  function words(zh, en) {
    return isZh() ? zh : en;
  }
  /**
   * @template {keyof HTMLElementTagNameMap} T
   * @param {T} tag
   * @param {string} [className]
   * @param {unknown} [text]
   * @returns {HTMLElementTagNameMap[T]}
   */
  function make(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = String(text);
    return node;
  }
  function endpoint(root, attribute) {
    var raw = clean(root.getAttribute(attribute));
    if (!raw) return '';
    try {
      var url = new URL(raw, API_ORIGIN);
      return url.origin === API_ORIGIN && url.pathname.indexOf(API_PREFIX) === 0
        ? url.toString()
        : '';
    } catch (_error) {
      return '';
    }
  }
  function setStatus(root, text, error) {
    var node = root.querySelector('[data-gcp-supplier-network-status]');
    if (node) node.textContent = text || '';
    root.setAttribute('data-gcp-supplier-network-state', error ? 'error' : 'ready');
  }
  function nativeSession() {
    var session = window.LittleGWebSession;
    return session && typeof session.authenticatedFetch === 'function' ? session : null;
  }
  async function requestJson(url, options) {
    if (!url) throw new Error('missing_endpoint');
    var session = nativeSession();
    if (!session) throw new Error('native_session_unavailable');
    var init = Object.assign({ headers: { Accept: 'application/json' } }, options || {});
    if (init.body && typeof init.body !== 'string') {
      init.body = JSON.stringify(init.body);
      init.headers = Object.assign({}, init.headers, { 'Content-Type': 'application/json' });
    }
    var response = await session.authenticatedFetch(url, init);
    var payload = await response.json().catch(function () {
      return {};
    });
    if (!response.ok || !payload || payload.ok !== true) {
      var error = new Error(
        clean(payload && payload.error) ||
          clean(payload && payload.message) ||
          'request_' + response.status
      );
      error.status = response.status;
      error.serverMessage = clean(payload && payload.message);
      throw error;
    }
    return payload;
  }
  function createKey() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function')
      return 'gcp-subcontract-' + window.crypto.randomUUID();
    return 'gcp-subcontract-' + Date.now() + '-' + Math.random().toString(36).slice(2);
  }
  function normalizeCapability(value) {
    return clean(value)
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9\u3400-\u9fff]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);
  }
  function capabilityIds(root) {
    var form = root.querySelector('[data-gcp-supplier-search-form]');
    var raw = clean(
      form && form.elements && form.elements.capabilities && form.elements.capabilities.value
    );
    var seen = Object.create(null);
    return raw
      .split(/[\n,]+/)
      .map(normalizeCapability)
      .filter(function (item) {
        if (!item || seen[item]) return false;
        seen[item] = true;
        return true;
      })
      .slice(0, 12);
  }
  function sourceOpportunityId(root) {
    var panel = root.querySelector('[data-gcp-gtender-opportunity]');
    return clean(panel && panel.getAttribute('data-tender-id')).slice(0, 240);
  }
  function clearDraftKey(form) {
    delete form.dataset.gcpSubcontractKey;
  }
  function removeSubcontractOptions(select) {
    if (!select || !select.options) return;
    Array.prototype.slice.call(select.options).forEach(function (option) {
      if (
        option.hasAttribute('data-gcp-subcontract-child') ||
        clean(option.dataset && option.dataset.gcpProjectRelationship) === 'subcontract' ||
        clean(option.value).indexOf('job_sub_') === 0 ||
        clean(option.textContent).indexOf('gcitypartners-subcontract-work-package') >= 0
      )
        option.remove();
    });
  }
  function validateRfqBeforeWorkPackage(root, form) {
    if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
      if (typeof form.reportValidity === 'function') form.reportValidity();
      return false;
    }
    var minRaw = clean(form.elements.budget_min && form.elements.budget_min.value);
    var maxRaw = clean(form.elements.budget_max && form.elements.budget_max.value);
    var min = minRaw ? Number(minRaw) : null;
    var max = maxRaw ? Number(maxRaw) : null;
    if (
      (min !== null && (!Number.isFinite(min) || min < 0)) ||
      (max !== null && (!Number.isFinite(max) || max < 0)) ||
      (min !== null && max !== null && max < min)
    ) {
      setStatus(root, words('請檢查分包預算範圍。', 'Check the subcontract budget range.'), true);
      return false;
    }
    var dueRaw = clean(form.elements.due_at && form.elements.due_at.value);
    if (dueRaw) {
      var due = new Date(dueRaw);
      if (Number.isNaN(due.getTime()) || due.getTime() <= Date.now()) {
        setStatus(
          root,
          words('報價截止時間必須晚於現在。', 'Quote deadline must be in the future.'),
          true
        );
        return false;
      }
    }
    return true;
  }
  function activate(root) {
    var form = root.querySelector('[data-gcp-supplier-search-form]');
    if (!form || !form.elements || !form.elements.availability) return;
    form.elements.availability.value = 'available';
    form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  }
  async function createWorkPackageAndContinue(root, form, event) {
    if (form.dataset.gcpSubcontractPass === 'true') {
      delete form.dataset.gcpSubcontractPass;
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (form.dataset.gcpSubcontractBusy === 'true' || form.getAttribute('aria-busy') === 'true')
      return;

    if (!validateRfqBeforeWorkPackage(root, form)) return;

    var select = form.elements.job_id;
    var selectedOption = select && select.options ? select.options[select.selectedIndex] : null;
    var projectRelationship = clean(
      selectedOption && selectedOption.dataset && selectedOption.dataset.gcpProjectRelationship
    );
    var primeJobId = clean(select && select.value);
    var providerId = clean(
      form.elements.target_public_profile_id && form.elements.target_public_profile_id.value
    );
    var title = clean(form.elements.title && form.elements.title.value);
    if (
      !primeJobId ||
      !providerId ||
      !title ||
      projectRelationship === 'subcontract' ||
      primeJobId.indexOf('job_sub_') === 0
    ) {
      setStatus(
        root,
        words(
          '請先選擇 Prime Project、合作方並填寫分包工作包標題。',
          'Select a Prime Project and partner, then add a subcontract work-package title.'
        ),
        true
      );
      return;
    }

    var createEndpoint = endpoint(root, 'data-subcontract-create-endpoint');
    if (!createEndpoint) {
      setStatus(
        root,
        words('分包工作包服務尚未配置。', 'Subcontract work-package service is not configured.'),
        true
      );
      return;
    }

    var key = clean(form.dataset.gcpSubcontractKey) || createKey();
    form.dataset.gcpSubcontractKey = key;
    form.dataset.gcpSubcontractBusy = 'true';
    setStatus(
      root,
      words(
        '正在建立 Prime Project 下的分包工作包…',
        'Creating a subcontract work package under the Prime Project…'
      )
    );

    try {
      var result = await requestJson(createEndpoint, {
        method: 'POST',
        headers: { 'Idempotency-Key': key },
        body: {
          parent_job_id: primeJobId,
          title: title,
          scope: clean(form.elements.scope && form.elements.scope.value) || null,
          required_capability_ids: capabilityIds(root),
          source_opportunity_id: sourceOpportunityId(root) || null,
        },
      });
      var childJobId = clean(result && result.work_package && result.work_package.job_id);
      if (!childJobId || childJobId.indexOf('job_sub_') !== 0)
        throw new Error('missing_subcontract_job_id');

      var childOption = document.createElement('option');
      childOption.value = childJobId;
      childOption.textContent = 'Subcontract work package · …' + childJobId.slice(-6);
      childOption.hidden = true;
      childOption.setAttribute('data-gcp-subcontract-child', '');
      childOption.setAttribute('data-gcp-project-relationship', 'subcontract');
      select.appendChild(childOption);
      select.value = childJobId;

      setStatus(
        root,
        words(
          '分包工作包已建立，正在發出 RFQ…',
          'Subcontract work package created. Sending the RFQ…'
        )
      );
      form.dataset.gcpSubcontractPass = 'true';
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));

      select.value = primeJobId;
      childOption.remove();
    } catch (error) {
      var code = clean(error && error.message);
      var message =
        code === 'prime_project_not_eligible'
          ? words(
              '此項目不能作為 Prime Project 建立分包工作包。',
              'This project is not eligible to create a subcontract work package.'
            )
          : code === 'subcontract_idempotency_conflict'
            ? words(
                '分包內容已改變，請重新選擇或刷新後再試。',
                'The subcontract request changed. Refresh or reselect the project before trying again.'
              )
            : code === 'native_session_unavailable'
              ? words(
                  '請先登入 GCityPartners 帳戶。',
                  'Sign in to your GCityPartners account first.'
                )
              : words(
                  '暫時無法建立分包工作包。',
                  'The subcontract work package could not be created.'
                );
      setStatus(root, message, true);
    } finally {
      delete form.dataset.gcpSubcontractBusy;
    }
  }

  function mount(root) {
    if (!root || mounted.has(root)) return;
    mounted.add(root);
    var searchForm = root.querySelector('[data-gcp-supplier-search-form]');
    var rfqForm = root.querySelector('[data-gcp-rfq-form]');
    if (!searchForm || !rfqForm) return;

    root.querySelectorAll('[data-gcp-capacity-activation]').forEach(function (node) {
      node.remove();
    });

    const panel = make('div', 'gcp-supplier-network__capacity-activation');
    panel.setAttribute('data-gcp-capacity-activation', '');
    var copy = make('div');
    copy.appendChild(
      make(
        'strong',
        '',
        words('Prime Project 找分包合作方', 'Find subcontract partners for a Prime Project')
      )
    );
    copy.appendChild(
      make(
        'p',
        'gcp-supplier-network__fine',
        words(
          '選擇主專案與合作方，填寫分包範圍後提交。系統會先建立工作包，再發出 RFQ；合作方仍需提交報價。「可接單」是合作夥伴申報狀態，不代表已預留產能。',
          'Select a parent project and partner, then define and submit the subcontract scope. A work package is created before the RFQ is sent; the partner still needs to quote. Available is supplier-declared and does not reserve capacity.'
        )
      )
    );
    panel.appendChild(copy);
    var button = make(
      'button',
      'gcp-dashboard__button',
      words('只看目前可接單合作方', 'Show partners available now')
    );
    button.type = 'button';
    button.setAttribute('data-gcp-capacity-available', '');
    button.addEventListener('click', function () {
      activate(root);
    });
    panel.appendChild(button);
    searchForm.parentNode.insertBefore(panel, searchForm);

    rfqForm.addEventListener(
      'submit',
      function (event) {
        void createWorkPackageAndContinue(root, rfqForm, event);
      },
      true
    );
    ['job_id', 'title', 'scope'].forEach(function (name) {
      var field = rfqForm.elements[name];
      if (!field) return;
      field.addEventListener(name === 'job_id' ? 'change' : 'input', function () {
        clearDraftKey(rfqForm);
      });
    });
    if (searchForm.elements.capabilities)
      searchForm.elements.capabilities.addEventListener('input', function () {
        clearDraftKey(rfqForm);
      });

    var select = rfqForm.elements.job_id;
    if (select) {
      var observer = new MutationObserver(function () {
        removeSubcontractOptions(select);
      });
      observer.observe(select, { childList: true });
      removeSubcontractOptions(select);
    }
  }

  /** @param {ParentNode | null} [scope] */
  function mountAll(scope) {
    (scope || document).querySelectorAll(ROOT).forEach(mount);
  }

  window.GCityPartnersCapacityActivation = Object.freeze({
    mountAll: mountAll,
    activate: activate,
    normalizeCapability: normalizeCapability,
  });
  document.addEventListener('shopify:section:load', function (event) {
    mountAll(event.target || document);
  });
  if (document.readyState === 'loading')
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        mountAll(document);
      },
      { once: true }
    );
  else mountAll(document);
})(window, document);
